import {
  LayoutDashboard,
  AlertCircle,
  Settings,
  Shield,
  ChevronUp,
  User,
  LogOut,
  CreditCard,
  ScanSearch,
  Plus,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import type { FeatureFlags } from "@/types/features";
import { PRODUCTS, type ProductKey } from "@/constants/products";
import { isValidProductKey, getDefaultProduct } from "@/utils/products";

// Menu items grouped by product
const menuItemsByProduct: Record<
  ProductKey,
  Array<{
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
    featureFlag: keyof FeatureFlags;
  }>
> = {
  redteam: [
    {
      title: "New Scan",
      url: "/compliance",
      icon: Plus,
      featureFlag: "demo_flow",
    },
    {
      title: "AI Compliance Scans",
      url: "/llm-scans",
      icon: ScanSearch,
      featureFlag: "llm_scans",
    },
  ],
  security: [
    {
      title: "Dashboard",
      url: "/security",
      icon: LayoutDashboard,
      featureFlag: "security_dashboard",
    },
    {
      title: "Issues",
      url: "/issues",
      icon: AlertCircle,
      featureFlag: "issues",
    },
    {
      title: "Alerts & Policies",
      url: "/alerts",
      icon: Shield,
      featureFlag: "alerts",
    },
  ],
};

/**
 * Renders the application sidebar with feature-flag filtered navigation and a user menu that provides profile links and logout.
 *
 * The navigation list is organized by product (Red Team, Security) and filtered by feature flags.
 * Only products the user has access to are shown. The user menu displays the current user's name
 * and email and includes links to Profile, Billing, Settings, and a Log out action.
 *
 * @returns The sidebar React element.
 */
export function AppSidebar() {
  const { user, logout, isLoading } = useAuth0();
  const { data: featureFlags, products } = useFeatureFlags();

  // Get user display name and email from Auth0
  const userName = user?.name || user?.nickname || "User";
  const userEmail = user?.email || "user@example.com";

  // Product switcher state - default to first available product or redteam
  const [selectedProduct, setSelectedProduct] = useState<ProductKey | null>(null);

  // Initialize and update selected product based on available products
  useEffect(() => {
    // Compute desired product based on current products
    let desiredProduct: ProductKey | null = null;

    if (products.length > 0) {
      // Try to get saved product from localStorage (with validation)
      const savedProductValue = localStorage.getItem("selectedProduct");
      if (savedProductValue && isValidProductKey(savedProductValue) && products.includes(savedProductValue)) {
        desiredProduct = savedProductValue;
      } else {
        // Fallback to default product
        desiredProduct = getDefaultProduct(products);
      }
    }

    // Use functional update to read current state without including it in dependencies
    setSelectedProduct((currentProduct) => {
      // Only update if desired product differs from current
      if (desiredProduct !== currentProduct) {
        if (desiredProduct !== null) {
          localStorage.setItem("selectedProduct", desiredProduct);
          return desiredProduct;
        } else {
          // No products available, clear selection
          localStorage.removeItem("selectedProduct");
          return null;
        }
      }
      // No change needed
      return currentProduct;
    });
  }, [products]); // Removed selectedProduct from dependencies to avoid cycle

  const handleProductChange = (productKey: ProductKey) => {
    setSelectedProduct(productKey);
    localStorage.setItem("selectedProduct", productKey);
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  // Get available products for switcher
  const availableProducts = (Object.keys(PRODUCTS) as ProductKey[]).filter((productKey) =>
    products.includes(productKey)
  );

  // Get current product display name
  const currentProductName =
    selectedProduct && PRODUCTS[selectedProduct]
      ? PRODUCTS[selectedProduct].displayName
      : "Platform";

  // Get menu items for a product, filtered by feature flags
  const getProductMenuItems = (productKey: ProductKey) => {
    const items = menuItemsByProduct[productKey];
    if (!featureFlags) {
      return [];
    }
    return items.filter((item) => featureFlags[item.featureFlag] === true);
  };

  // Determine which products to show - only show the selected product's menu items
  const visibleProducts = selectedProduct
    ? [selectedProduct].filter((productKey) => {
        // Show product section if user has the product and it has visible items
        const hasProduct = products.includes(productKey);
        const hasVisibleItems = getProductMenuItems(productKey).length > 0;
        return hasProduct && hasVisibleItems;
      })
    : [];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            {availableProducts.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="w-full data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    data-tour="sidebar-logo"
                  >
                    <img
                      src="/logo.png"
                      alt="EarlyCore Logo"
                      className="size-8 rounded-lg object-contain"
                    />
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">EarlyCore</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {currentProductName}
                      </span>
                    </div>
                    <ChevronDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="bottom"
                  className="w-[--radix-popper-anchor-width] mt-2"
                  align="start"
                >
                  {availableProducts.map((productKey) => {
                    const product = PRODUCTS[productKey];
                    const ProductIcon = product.icon;
                    const isSelected = selectedProduct === productKey;

                    return (
                      <DropdownMenuItem
                        key={productKey}
                        onClick={() => handleProductChange(productKey)}
                        className={`cursor-pointer ${isSelected ? "bg-sidebar-accent" : ""}`}
                      >
                        <ProductIcon className="mr-2 h-4 w-4" />
                        <span>{product.displayName}</span>
                        {isSelected && <ChevronDown className="ml-auto h-4 w-4 rotate-180" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SidebarMenuButton size="lg" asChild>
                <Link to="/" className="flex items-center gap-2" data-tour="sidebar-logo">
                  <img
                    src="/logo.png"
                    alt="EarlyCore Logo"
                    className="size-8 rounded-lg object-contain"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">EarlyCore</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {currentProductName}
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {visibleProducts.map((productKey) => {
          const items = getProductMenuItems(productKey);

          return (
            <SidebarGroup key={productKey}>
              <SidebarGroupContent>
                <SidebarMenu data-tour={productKey === "redteam" ? "sidebar-nav" : undefined}>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        data-tour={item.url === "/compliance" ? "compliance-menu" : undefined}
                      >
                        <Link to={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  disabled={isLoading}
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                    <User className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-[--radix-popper-anchor-width] mb-2">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/billing" className="cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>Billing</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
